import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DraggableItem } from "@repo/ui/DraggableList/draggableItem";
import { Size } from "@repo/ui/helpers/helpers";
import { useDraggableList } from "@repo/ui/hooks/useDraggableList";
import { IconPlus, IconShare2 } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { QuestionsManagerCell } from "./questionsManagerCell.js";
import {
  questionsActions,
  type QuestionsDispatch,
  type QuestionWithTemporaryId,
} from "./questionsStore.js";

export interface QuestionsManagerTableProps {
  questions: readonly QuestionWithTemporaryId[];
  onQuestionsChange: QuestionsDispatch;
  onImportClick?: () => void;
}

const MAX_INTERVIEW_QUESTIONS = 25;

export const QuestionsManagerTable: FC<QuestionsManagerTableProps> = (
  props,
) => {
  const [autoFocusIndex, setAutoFocusIndex] = useState<number | undefined>(
    undefined,
  );

  const {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleDragLeave,
    dragOverIndex,
  } = useDraggableList({
    items: props.questions,
    onReorder: (newQuestions) =>
      props.onQuestionsChange(questionsActions.reordered(newQuestions)),
  });

  const handlePasteQuestions = (index: number, lines: string[]) => {
    if (lines.length === 0) return;

    const availableSlots = MAX_INTERVIEW_QUESTIONS - props.questions.length;
    const linesToInsert = lines.slice(0, availableSlots);

    linesToInsert.forEach((line, i) => {
      const insertIndex = index + i + 1;
      props.onQuestionsChange(
        questionsActions.inserted({ index: insertIndex }),
      );
      props.onQuestionsChange(
        questionsActions.updated({
          index: insertIndex,
          basicQuestion: { question: line },
        }),
      );
    });

    // Focus the question after the last inserted question
    setAutoFocusIndex(index + linesToInsert.length + 1);
  };

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <div>
          {props.questions.map((question, index) => (
            <DraggableItem
              dragOverIndex={dragOverIndex}
              index={index}
              isDraggable={allowed}
              key={question.id}
              onDragEnd={handleDragEnd}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
            >
              <QuestionsManagerCell
                onChange={(basicQuestion) =>
                  props.onQuestionsChange(
                    questionsActions.updated({
                      index,
                      basicQuestion,
                    }),
                  )
                }
                onEnterKeyDown={() => {
                  const insertIndex = index + 1;
                  props.onQuestionsChange(
                    questionsActions.inserted({ index: insertIndex }),
                  );
                  setAutoFocusIndex(insertIndex);
                }}
                onPasteQuestions={(lines) => handlePasteQuestions(index, lines)}
                onRemove={() =>
                  props.onQuestionsChange(questionsActions.deleted({ index }))
                }
                question={question}
                questionNumber={index + 1}
                autoFocus={autoFocusIndex === index}
              />
            </DraggableItem>
          ))}

          <div className="m-3 flex flex-row justify-between space-x-3">
            <Button
              icon={<IconPlus className="h-4 w-4" />}
              isDisabled={
                props.questions.length >= MAX_INTERVIEW_QUESTIONS || !allowed
              }
              onClick={() => {
                props.onQuestionsChange(questionsActions.added());
                setAutoFocusIndex(props.questions.length);
              }}
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
            >
              Add question
            </Button>
            {props.onImportClick ? (
              <Button
                icon={<IconShare2 className="h-4 w-4" />}
                onClick={props.onImportClick}
                size={Size.MEDIUM}
                variant={ButtonVariant.Secondary}
                isDisabled={!allowed}
              >
                Bulk import
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Can>
  );
};
